import * as Sentry from '@sentry/react';
import { Component, ReactNode } from 'react';

import { Button } from '@/app/shared/ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    // Define a state variable to track whether is an error or not
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // send to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    console.log({ error, errorInfo });
  }

  render() {
    // Check if the error is thrown
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className={'min-h-screen flex min-w-screen flex-col items-center justify-center gap-6'}>
          <h1 className={'text-center text-[36px]'}>Oops! Something went wrong.</h1>
          <div className='flex gap-2'>
            <Button
              onClick={() => this.setState({ hasError: false })}
              variant='primary'
              className={'px-6 py-3'}
            >
              Try again?
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
              variant='secondary'
              className='px-6 py-3'
            >
              Go to Home
            </Button>
          </div>
        </div>
      );
    }

    // Return children components in case of no error
    return this.props.children;
  }
}

export default ErrorBoundary;
